import { objectMapValues } from "@redotech/util/object";
import {
  AttributionEvidenceType,
  AttributionTouchpoint,
  type AttributionSettings,
} from "./attribution-settings";

/**
 * The touchpoint each evidence type belongs to. Typed as a Record over every
 * {@link AttributionEvidenceType}, so a new evidence type can't compile until
 * it's assigned to a touchpoint (and maps to exactly one).
 */
const EVIDENCE_TOUCHPOINT: Record<
  AttributionEvidenceType,
  AttributionTouchpoint
> = {
  [AttributionEvidenceType.EMAIL_SEND]: AttributionTouchpoint.EMAIL,
  [AttributionEvidenceType.EMAIL_DELIVERED]: AttributionTouchpoint.EMAIL,
  [AttributionEvidenceType.EMAIL_OPEN]: AttributionTouchpoint.EMAIL,
  [AttributionEvidenceType.EMAIL_CLICK]: AttributionTouchpoint.EMAIL,
  [AttributionEvidenceType.SMS_SEND]: AttributionTouchpoint.SMS,
  [AttributionEvidenceType.SMS_CLICK]: AttributionTouchpoint.SMS,
};

/** Evidence types that make up a touchpoint, in declaration order. */
export function touchpointEvidenceTypes(
  touchpoint: AttributionTouchpoint,
): AttributionEvidenceType[] {
  return Object.values(AttributionEvidenceType).filter(
    (evidenceType) => EVIDENCE_TOUCHPOINT[evidenceType] === touchpoint,
  );
}

/** Pass to {@link restrictToTouchpoints} for the holistic (unrestricted) view. */
export const ALL_ATTRIBUTION_TOUCHPOINTS = Object.values(AttributionTouchpoint);

/**
 * Team settings overlaid so only `keep` touchpoints stay in play — every other
 * touchpoint's inferred evidence and direct config are switched off.
 */
export function restrictToTouchpoints(
  settings: AttributionSettings,
  keep: readonly AttributionTouchpoint[],
): AttributionSettings {
  const excluded = ALL_ATTRIBUTION_TOUCHPOINTS.filter(
    (tp) => !keep.includes(tp),
  );
  const offEvidence = new Set<AttributionEvidenceType>(
    excluded.flatMap(touchpointEvidenceTypes),
  );
  const offTouchpoints = new Set<AttributionTouchpoint>(excluded);
  return objectMapValues(settings, (config) => ({
    ...config,
    inferred: objectMapValues(config.inferred, (cfg, evidenceType) =>
      cfg && offEvidence.has(evidenceType) ? { ...cfg, enabled: false } : cfg,
    ),
    direct: objectMapValues(config.direct, (cfg, touchpoint) =>
      cfg && offTouchpoints.has(touchpoint) ? { ...cfg, enabled: false } : cfg,
    ),
  }));
}

/**
 * The evidence types each touchpoint's DIRECT attribution draws from: the send
 * (the message that carried the code) and the click (same code, same
 * touchpoint id — so it dedupes with the send, and stands in when the send
 * event wasn't recorded).
 */
export const TOUCHPOINT_DIRECT_EVENTS: Record<
  AttributionTouchpoint,
  AttributionEvidenceType[]
> = {
  [AttributionTouchpoint.EMAIL]: [
    AttributionEvidenceType.EMAIL_SEND,
    AttributionEvidenceType.EMAIL_CLICK,
  ],
  [AttributionTouchpoint.SMS]: [
    AttributionEvidenceType.SMS_SEND,
    AttributionEvidenceType.SMS_CLICK,
  ],
};
